// JSXWrappers.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

/**
 * A collection of wrapper components that can be used in JSX files
 * to provide hook functionality in static JSX
 */

/**
 * StateWrapper - Provides useState hook functionality in JSX
 * @param {Object} props
 * @param {any} props.initialValue - Initial state value
 * @param {Function} props.children - Render function that receives [state, setState]
 */
export const StateWrapper = ({ initialValue, children }) => {
  const [state, setState] = useState(initialValue);
  
  return children(state, setState);
};

/**
 * EffectWrapper - Provides useEffect hook functionality in JSX
 * @param {Object} props
 * @param {Function} props.effect - Effect function to run
 * @param {Array} props.deps - Dependencies array
 * @param {Function|React.ReactNode} props.children - Children or render function
 */
export const EffectWrapper = ({ effect, deps = [], children }) => {
  useEffect(() => {
    return effect();
  }, deps);
  
  if (typeof children === 'function') {
    return children();
  }
  
  return children;
};

/**
 * RefWrapper - Provides useRef hook functionality in JSX
 * @param {Object} props
 * @param {any} props.initialValue - Initial ref value
 * @param {Function} props.children - Render function that receives ref
 */
export const RefWrapper = ({ initialValue, children }) => {
  const ref = useRef(initialValue);
  
  return children(ref);
};

/**
 * MemoWrapper - Provides useMemo hook functionality in JSX
 * @param {Object} props
 * @param {Function} props.compute - Function to compute memoized value
 * @param {Array} props.deps - Dependencies array
 * @param {Function} props.children - Render function that receives memoized value
 */
export const MemoWrapper = ({ compute, deps = [], children }) => {
  const memoizedValue = useMemo(() => compute(), deps);
  
  return children(memoizedValue);
};

/**
 * CallbackWrapper - Provides useCallback hook functionality in JSX
 * @param {Object} props
 * @param {Function} props.callback - Callback function
 * @param {Array} props.deps - Dependencies array
 * @param {Function} props.children - Render function that receives memoized callback
 */
export const CallbackWrapper = ({ callback, deps = [], children }) => {
  const memoizedCallback = useCallback(callback, deps);
  
  return children(memoizedCallback);
};

/**
 * TimerWrapper - Higher-level wrapper for interval timers
 * @param {Object} props
 * @param {number} props.interval - Interval in milliseconds
 * @param {Function} props.onTick - Function to call on each interval
 * @param {boolean} props.active - Whether the timer is active
 * @param {Function|React.ReactNode} props.children - Children or render function
 */
export const TimerWrapper = ({ interval = 1000, onTick, active = true, children }) => {
  const [tick, setTick] = useState(0);
  
  useEffect(() => {
    if (!active) return;
    
    const timer = setInterval(() => {
      setTick(prev => prev + 1);
      if (onTick) onTick();
    }, interval);
    
    return () => clearInterval(timer);
  }, [interval, active, onTick]);
  
  if (typeof children === 'function') {
    return children(tick);
  }
  
  return children;
};

/**
 * AnimationWrapper - Provides requestAnimationFrame based animation
 * @param {Object} props
 * @param {Function} props.onFrame - Function to call on each frame
 * @param {boolean} props.active - Whether the animation is active
 * @param {Function|React.ReactNode} props.children - Children or render function
 */
export const AnimationWrapper = ({ onFrame, active = true, children }) => {
  const [frame, setFrame] = useState(0);
  const requestRef = useRef();
  
  const animate = useCallback(() => {
    setFrame(prev => prev + 1);
    if (onFrame) onFrame();
    requestRef.current = requestAnimationFrame(animate);
  }, [onFrame]);
  
  useEffect(() => {
    if (active) {
      requestRef.current = requestAnimationFrame(animate);
    }
    return () => cancelAnimationFrame(requestRef.current);
  }, [active, animate]);
  
  if (typeof children === 'function') {
    return children(frame);
  }
  
  return children;
};

/**
 * FetchWrapper - Handles data fetching with loading and error states
 * @param {Object} props
 * @param {string} props.url - URL to fetch
 * @param {Object} props.options - Fetch options
 * @param {Function} props.children - Render function that receives { data, loading, error }
 */
export const FetchWrapper = ({ url, options, children }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(url, options);
        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }
        const result = await response.json();
        setData(result);
        setError(null);
      } catch (err) {
        setError(err.message);
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [url, options && JSON.stringify(options)]);
  
  return children({ data, loading, error });
};

/**
 * FormWrapper - Provides form state management
 * @param {Object} props
 * @param {Object} props.initialValues - Initial form values
 * @param {Function} props.onSubmit - Submit handler
 * @param {Function} props.children - Render function that receives form helpers
 */
export const FormWrapper = ({ initialValues = {}, onSubmit, children }) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setValues(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);
  
  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
  }, []);
  
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await onSubmit(values);
    } catch (err) {
      setErrors(err || {});
    } finally {
      setSubmitting(false);
    }
  }, [values, onSubmit]);
  
  const resetForm = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
  }, [initialValues]);
  
  return children({
    values,
    errors,
    touched,
    submitting,
    handleChange,
    handleBlur,
    handleSubmit,
    resetForm,
    setValues,
    setErrors,
    setTouched
  });
};

/**
 * LocalStorageWrapper - Persists state to localStorage
 * @param {Object} props
 * @param {string} props.key - localStorage key
 * @param {any} props.initialValue - Initial state value
 * @param {Function} props.children - Render function that receives [state, setState]
 */
export const LocalStorageWrapper = ({ key, initialValue, children }) => {
  const [state, setState] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });
  
  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, state]);
  
  return children(state, setState);
};

export default {
  StateWrapper,
  EffectWrapper,
  RefWrapper,
  MemoWrapper,
  CallbackWrapper,
  TimerWrapper,
  AnimationWrapper,
  FetchWrapper,
  FormWrapper,
  LocalStorageWrapper
};